const stringifyAndTrim = (value) => {
  return String(value ?? '').trim();
};

const VALID_PLACEHOLDERS = [
  '{name}',
  '{part}',
  '{operation}',
  '{op}',
  '{lot}',
  '{split}',
  '{sub}',
  '{description}',
];

// The built-in fallback formats. These are rendered strictly (every placeholder
// must resolve) because they contain literal text (e.g. "Op") that reads as
// broken when an adjacent placeholder is empty.
const DEFAULT_FORMATS = [
  '{part} Op {operation}',
  '{name} Op {operation}',
  '{part}',
  '{name}',
];

/**
 * Validates if a format string contains at least one valid placeholder.
 * Used to determine if a display name format is valid or if it contains unresolved placeholders.
 *
 * @param {string} fmt - The format string to validate
 * @returns {boolean} True if format contains at least one valid placeholder
 */
export const validateJobDisplayNameFormat = (fmt) => {
  const trimmed = stringifyAndTrim(fmt);
  return (
    trimmed.length > 0 &&
    VALID_PLACEHOLDERS.some((p) => {
      return trimmed.includes(p);
    })
  );
};

/**
 * Resolves placeholder values from a work order operation object.
 * Extracts name, part, operation, lot, and description from various nested properties.
 *
 * @param {Object} orderOp - Work order operation object
 * @returns {Object} Resolved values for all placeholders
 */
const resolveValues = (orderOp) => {
  const partOp = orderOp.partOperation ?? orderOp.matchingPartOperations?.[0];
  const { workOrder } = orderOp;

  const part = stringifyAndTrim(
    orderOp.partNumber ??
      orderOp.partName ??
      workOrder?.partNumber ??
      partOp?.partNumber ??
      partOp?.partName
  );

  const name = stringifyAndTrim(
    orderOp.name ?? workOrder?.workOrderId ?? orderOp.workOrderId
  );

  const operation = stringifyAndTrim(
    orderOp.sequenceNumber ?? orderOp.operationNum ?? partOp?.sequenceNumber
  );

  const lot = stringifyAndTrim(orderOp.lot ?? workOrder?.lot ?? partOp?.lot);

  const split = stringifyAndTrim(
    orderOp.split ?? workOrder?.split ?? partOp?.split
  );

  const sub = stringifyAndTrim(orderOp.sub ?? workOrder?.sub ?? partOp?.sub);

  const description = stringifyAndTrim(
    orderOp.description ?? partOp?.description ?? workOrder?.description
  );

  return {
    name,
    part,
    operation,
    op: operation,
    lot,
    split,
    sub,
    description,
  };
};

/**
 * Substitutes resolved values into a format template.
 * Unresolved placeholders become empty strings.
 *
 * @param {string} fmt - The format template
 * @param {Object} values - Resolved placeholder values
 * @returns {string} Interpolated string (not yet trimmed/collapsed)
 */
const applyFormat = (fmt, values) => {
  return fmt
    .replace(/\{name\}/g, values.name)
    .replace(/\{part\}/g, values.part)
    .replace(/\{operation\}/g, values.operation)
    .replace(/\{op\}/g, values.op)
    .replace(/\{lot\}/g, values.lot)
    .replace(/\{split\}/g, values.split)
    .replace(/\{sub\}/g, values.sub)
    .replace(/\{description\}/g, values.description);
};

/**
 * Formats a work order operation display name using a format template.
 * Falls back to default formats if the provided format is invalid or placeholders can't be resolved.
 *
 * @param {Object} orderOp - Work order operation object
 * @param {string|null} jobDisplayNameFormat - Format template with placeholders (e.g., '{part} Op {operation}')
 * @returns {string|null} Formatted display name, or null if format doesn't match available data
 *
 * @example
 * formatOrderOpName(orderOp, '{part} Op {operation}')
 * // Returns: "ABC-123 Op 10"
 */
export const formatOrderOpName = (orderOp, jobDisplayNameFormat = null) => {
  if (!orderOp) return '';

  if (!validateJobDisplayNameFormat(jobDisplayNameFormat)) {
    return (
      formatOrderOpName(orderOp, '{part} Op {operation}') ??
      formatOrderOpName(orderOp, '{name} Op {operation}') ??
      formatOrderOpName(orderOp, '{part}') ??
      formatOrderOpName(orderOp, '{name}') ??
      ''
    );
  }

  const values = resolveValues(orderOp);

  const usedPlaceholders = jobDisplayNameFormat.match(/\{(\w+)\}/g) || [];
  const allResolved = usedPlaceholders.every((placeholder) => {
    const key = placeholder.slice(1, -1);
    return values[key]?.length > 0;
  });

  const isDefaultFormat = DEFAULT_FORMATS.includes(jobDisplayNameFormat);

  if (!allResolved) {
    // For custom company formats, render whatever placeholders resolve, drop
    // the empty ones, and collapse the whitespace they leave behind. This keeps
    // formats like "{lot} {split} {sub}" useful even when a work order has no
    // split or sub (a common case in ERP data).
    if (!isDefaultFormat) {
      const partial = applyFormat(jobDisplayNameFormat, values)
        .replace(/\s+/g, ' ')
        .trim();

      if (partial) {
        return partial;
      }

      // Nothing resolved at all — fall back to the default formats.
      return (
        formatOrderOpName(orderOp, '{part} Op {operation}') ??
        formatOrderOpName(orderOp, '{name} Op {operation}') ??
        formatOrderOpName(orderOp, '{part}') ??
        formatOrderOpName(orderOp, '{name}') ??
        ''
      );
    }

    // If even a default format cannot be fully resolved, return an empty string.
    return '';
  }

  const result = applyFormat(jobDisplayNameFormat, values).trim();

  return result || '';
};
